import React from 'react';

export interface ApiClientOptions {
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT';
  headers?: any;
  withApiHost?: boolean;
  body?: string | FormData | File;
  credentials?: RequestCredentials;
}

export type TApiAuthClient = {
  headers: Record<string, string | undefined>;
  setAccessToken: (token: string) => void;
  fetch: (endpoint: string, options?: ApiClientOptions | undefined) => Promise<Response>;
};

const fiveMinuets = 60 * 5000;

const useLoginDetector = ({ apiAuthClient }: { apiAuthClient: TApiAuthClient }): void => {
  const isUserLoggedInChecker = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkIfUserIsLoggedInOnInterval = async () => {
    const checkIfLoggedIn = async () => {
      // wrapping with try, in case network request fails (no internet for example)
      // to keep the process from crashing. we want it to continue to call ping after crash as well.
      try {
        const res = await apiAuthClient.fetch('/api/v1/auth/ping');
        if (res?.status === 401) {
          window.location.replace(`${window.location.origin}/api/auth/logout`);
          if (isUserLoggedInChecker.current) {
            return clearTimeout(isUserLoggedInChecker.current);
          }
        }
      } catch {
        // eslint-disable-next-line no-console
        console.log('network request failed on login detector');
      }

      isUserLoggedInChecker.current = setTimeout(checkIfLoggedIn, fiveMinuets);
    };

    isUserLoggedInChecker.current = setTimeout(checkIfLoggedIn, fiveMinuets);
  };

  React.useEffect(() => {
    if (!isUserLoggedInChecker.current) {
      checkIfUserIsLoggedInOnInterval();
    }
    return () => {
      if (isUserLoggedInChecker.current) {
        clearTimeout(isUserLoggedInChecker.current);
        isUserLoggedInChecker.current = null;
      }
    };
  }, []);
};

export default useLoginDetector;
